import type { ComponentType } from "react";

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { formatCurrency } from "@/utils";

interface TablePaginationProps {
  /** Sahifa tartib raqami — 0 dan (TanStack bilan bir xil). */
  pageIndex: number;
  setPageIndex: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  totalPages: number;
  total?: number;
  pageSizeOptions?: number[];
}

/** Sonlar panelning qolgan joyidagidek probel bilan guruhlanadi: `1 250`. */
const grouped = (value: number) => formatCurrency(value, "space");

export function TablePagination({
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  totalPages,
  total,
  pageSizeOptions = [20, 50, 100],
}: Readonly<TablePaginationProps>) {
  const { t } = useTranslation();

  // Ro'yxatda yo'q hajm (masalan, jurnaldagi 30) ham tanlovda ko'rinsin —
  // aks holda tanlov bo'sh turardi.
  const sizes = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPageIndex(0);
  };

  const canGoBack = pageIndex > 0;
  const canGoForward = pageIndex + 1 < totalPages;

  const navButton = (
    onClick: () => void,
    disabled: boolean,
    label: string,
    Icon: ComponentType,
  ) => (
    <Button
      /* `type` ataylab: tugma forma ichida bo'lsa standart `submit` bo'lib
         qoladi va sahifani almashtirish o'rniga formani yuborib yuborardi. */
      type="button"
      variant="outline"
      size="icon"
      className="flex size-8"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <Icon />
    </Button>
  );

  return (
    <div className="flex items-center justify-between gap-5 pt-2">
      {/* Sahifa hajmi */}
      <div className="flex-1 text-sm">
        <div className="flex items-center gap-2">
          <p className="hidden text-sm font-medium sm:block">{t("TABLE.ROWS_PER_PAGE")}</p>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger
              size="sm"
              aria-label={t("TABLE.ROWS_PER_PAGE")}
              className="bg-background w-18.75 cursor-pointer"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {sizes.map(size => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sahifalar */}
      <div className="flex items-center gap-5">
        <div className="hidden text-sm font-medium tabular-nums sm:block">
          {t("TABLE.PAGE_OF", {
            page: grouped(pageIndex + 1),
            total: grouped(Math.max(totalPages, 1)),
          })}
          {total !== undefined ? ` (${t("TABLE.TOTAL", { count: grouped(total) })})` : ""}
        </div>

        <div className="flex items-center gap-1">
          {navButton(() => setPageIndex(0), !canGoBack, t("TABLE.FIRST_PAGE"), ChevronsLeft)}
          {navButton(
            () => setPageIndex(pageIndex - 1),
            !canGoBack,
            t("TABLE.PREVIOUS_PAGE"),
            ChevronLeft,
          )}
          {navButton(
            () => setPageIndex(pageIndex + 1),
            !canGoForward,
            t("TABLE.NEXT_PAGE"),
            ChevronRight,
          )}
          {navButton(
            () => setPageIndex(totalPages - 1),
            !canGoForward,
            t("TABLE.LAST_PAGE"),
            ChevronsRight,
          )}
        </div>
      </div>
    </div>
  );
}
